import { Link } from 'react-router-dom'
import { hideMarkedText } from '@helpers/getMark'
import type { NavbarExpertisePage } from './nav.types'
import { EXPERTISES_SUBMENU_ID, EXPERTISES_TRIGGER_ID } from './nav.constants'
import { useExpertisesDropdown } from './useExpertisesDropdown'

type ExpertisesDropdown = ReturnType<typeof useExpertisesDropdown>

type NavbarExpertisesBlockProps = ExpertisesDropdown & {
    menubarIndex: number
    label: string
    isExpertisesRoute: boolean
    expertisePages: NavbarExpertisePage[]
}

const submenuLinkClass =
    'block px-4 py-2 text-sm text-gray-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#0a8335] focus-visible:outline-offset-2 focus-visible:rounded-sm'

export function NavbarExpertisesBlock({
    menubarIndex,
    label,
    isExpertisesRoute,
    expertisePages,
    isDesktopMenubar,
    dropdownOpen,
    setDropdownOpen,
    dropdownRef,
    buttonRef,
    triggerRef,
    submenuPanelRef,
    expertiseTriggerTabIndex,
    toggleExpertisesDropdownFromTrigger,
    openDropdownDesktop,
    closeDropdownDesktop,
    handleExpertisesTriggerKeyDown,
    handleSubmenuLinkKeyDown,
    handleDropdownKeyDown,
    handleDropdownBlur,
}: NavbarExpertisesBlockProps) {
    return (
        <>
            <div
                className="relative"
                ref={buttonRef}
                onMouseEnter={openDropdownDesktop}
                onMouseLeave={closeDropdownDesktop}
            >
                <button
                    type="button"
                    {...(isDesktopMenubar ? { role: 'menuitem' } : {})}
                    data-menubar-index={menubarIndex}
                    id={EXPERTISES_TRIGGER_ID}
                    ref={triggerRef}
                    className={`rounded-full py-2 px-4 text-sm font-medium text-left w-full lg:w-auto focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#0a8335] focus-visible:outline-offset-2 focus-visible:rounded-sm ${
                        isExpertisesRoute ? 'force-hover' : ''
                    }`}
                    style={{
                        fontSize: '1rem',
                        fontWeight: 600,
                        lineHeight: '1.4',
                    }}
                    tabIndex={expertiseTriggerTabIndex}
                    aria-haspopup={isDesktopMenubar ? 'menu' : undefined}
                    aria-expanded={dropdownOpen}
                    aria-controls={EXPERTISES_SUBMENU_ID}
                    onClick={toggleExpertisesDropdownFromTrigger}
                    onKeyDown={handleExpertisesTriggerKeyDown}
                >
                    {label}
                </button>
            </div>
            <div
                className="relative"
                onMouseEnter={openDropdownDesktop}
                onMouseLeave={closeDropdownDesktop}
                onKeyDown={handleDropdownKeyDown}
                onBlur={handleDropdownBlur}
                ref={dropdownRef}
            >
                <div
                    id={EXPERTISES_SUBMENU_ID}
                    ref={submenuPanelRef}
                    className={`${
                        dropdownOpen ? 'block' : 'hidden'
                    } lg:absolute left-0 top-full mt-2 lg:mt-0 w-full lg:w-64 bg-white border border-gray-200 rounded-md shadow-lg z-10`}
                    aria-hidden={!dropdownOpen}
                    {...(isDesktopMenubar
                        ? {
                              role: 'menu',
                              'aria-orientation': 'vertical',
                          }
                        : { role: 'region' })}
                    aria-labelledby={EXPERTISES_TRIGGER_ID}
                >
                    <ul
                        className="py-2"
                        {...(isDesktopMenubar ? { role: 'none' } : {})}
                    >
                        <li
                            className="hover:bg-gray-100"
                            {...(isDesktopMenubar ? { role: 'none' } : {})}
                        >
                            <Link
                                to="/expertises"
                                {...(isDesktopMenubar
                                    ? { role: 'menuitem' }
                                    : {})}
                                className={submenuLinkClass}
                                tabIndex={dropdownOpen ? undefined : -1}
                                onKeyDown={(e) =>
                                    handleSubmenuLinkKeyDown(e, 0)
                                }
                                onClick={() => setDropdownOpen(false)}
                            >
                                Toutes les expertises
                            </Link>
                        </li>
                        {expertisePages.map((expertisePage, subIdx) => (
                            <li
                                key={expertisePage.id}
                                className="hover:bg-gray-100"
                                {...(isDesktopMenubar ? { role: 'none' } : {})}
                            >
                                <Link
                                    to={`/expertises/${expertisePage.attributes.urlSlug}`}
                                    {...(isDesktopMenubar
                                        ? { role: 'menuitem' }
                                        : {})}
                                    className={submenuLinkClass}
                                    tabIndex={dropdownOpen ? undefined : -1}
                                    onKeyDown={(e) =>
                                        handleSubmenuLinkKeyDown(e, subIdx + 1)
                                    }
                                    onClick={() => setDropdownOpen(false)}
                                >
                                    {hideMarkedText(
                                        expertisePage.attributes.title
                                    )}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </>
    )
}
